import { useTranslation } from "react-i18next";
import { ArrowLeft, ArrowRight } from "lucide-react";

import {
  ACT_LABELS,
  FINAL_ACT,
  actAfter,
  actBefore,
  actNumber,
  type ActId,
} from "@/components/projects/diagnostic/acts";

/**
 * O rodapé de cada ato: volta à esquerda, avança à direita (#852).
 *
 * Os dois nomeiam o destino, e não a direção. "Ato 3 · As provas" diz para onde
 * se vai; "Próximo" não diz nada, e numa apresentação quem conduz precisa saber
 * o que vem antes de clicar.
 *
 * O Ato 5 fecha com "Fim da devolutiva" em texto, **não em botão desabilitado**:
 * botão desabilitado promete que um dia habilita, e este não habilita nunca.
 */
export function ActFooterNav({
  act,
  onGo,
}: {
  act: ActId;
  onGo: (next: ActId) => void;
}) {
  const { t } = useTranslation();

  const previous = actBefore(act);
  const next = actAfter(act);

  return (
    <div
      data-testid="diagnostic-act-nav"
      className="mt-8 flex flex-wrap items-center justify-between gap-3 border-t border-[#e8ebf0] pt-5"
    >
      {previous ? (
        <button
          type="button"
          onClick={() => onGo(previous)}
          className="inline-flex items-center gap-2 rounded-md border border-[#d4dae3] px-3.5 py-2 text-[13px] font-semibold text-[#596b85] transition-colors hover:border-[#08254c] hover:text-[#08254c]"
        >
          <ArrowLeft aria-hidden className="h-3.5 w-3.5" />
          {t(ACT_LABELS[previous].label)}
        </button>
      ) : (
        <span />
      )}

      {next ? (
        <button
          type="button"
          onClick={() => onGo(next)}
          /*
            O avanço para o ato final é dourado, e os outros são azuis (#325).
            Não é enfeite: é o sinal de que ali está o clímax da devolutiva, o
            ato que fica trancado até o vendedor decidir abrir. Um botão igual
            aos outros não avisa ninguém de que a porta é diferente.
          */
          className={
            next === FINAL_ACT
              ? "inline-flex items-center gap-2 rounded-md bg-[#ffc83d] px-4 py-2 text-[13px] font-semibold text-[#08254c] transition-colors hover:bg-[#f0b92a]"
              : "inline-flex items-center gap-2 rounded-md bg-[#08254c] px-4 py-2 text-[13px] font-semibold text-white transition-colors hover:bg-[#0d356e]"
          }
        >
          {next === FINAL_ACT
            ? t("Final act · open the numbers")
            : t("Act {{act}} · {{label}}", {
                act: actNumber(next),
                label: t(ACT_LABELS[next].label),
              })}
          <ArrowRight aria-hidden className="h-3.5 w-3.5" />
        </button>
      ) : (
        <span className="font-mono text-[11px] uppercase tracking-[0.1em] text-[#b2bccb]">
          {t("End of the briefing")}
        </span>
      )}
    </div>
  );
}
